import {
  AnyAction,
  configureStore,
  ConfigureStoreOptions,
  Middleware,
  Reducer,
} from '@reduxjs/toolkit';
import { combineEpics, createEpicMiddleware, Epic } from 'redux-observable';

import { StoreConfiguration } from '../storeConfiguration.model';

type RootEpic = Epic<AnyAction, AnyAction, any>;
type AnyReducerMap = Record<string, Reducer<any, AnyAction>>;
type AnyStoreConfiguration = StoreConfiguration<
  AnyReducerMap,
  Middleware[],
  Epic[]
>;

type UnionToIntersection<T> = (
  T extends unknown ? (value: T) => void : never
) extends (value: infer I) => void
  ? I
  : never;

type CombinedReducerMap<TFeatures extends readonly AnyStoreConfiguration[]> =
  Extract<UnionToIntersection<TFeatures[number]['reducerMap']>, AnyReducerMap>;

type StateFromReducerMap<TReducerMap> = {
  [K in keyof TReducerMap]: TReducerMap[K] extends Reducer<infer TState, any>
    ? TState
    : never;
};

type FeatureRootState<TFeatures extends readonly AnyStoreConfiguration[]> =
  StateFromReducerMap<CombinedReducerMap<TFeatures>>;

export function createCombinedFeatureStore<
  const TFeatures extends readonly AnyStoreConfiguration[],
  TDeps extends Record<string, any>,
>(
  features: TFeatures,
  options: {
    dependencies: TDeps;
    preloadedState?: ConfigureStoreOptions<
      FeatureRootState<TFeatures>
    >['preloadedState'];
  },
) {
  let reducerMap = {} as CombinedReducerMap<TFeatures>;
  const middlewares: Middleware[] = [];
  const epicSet = new Set<RootEpic>();
  const { dependencies, preloadedState } = options;

  features.forEach((feature) => {
    reducerMap = {
      ...reducerMap,
      ...feature.reducerMap,
    } as CombinedReducerMap<TFeatures>;

    feature.middlewares.forEach((middleware: Middleware) => {
      middlewares.push(middleware);
    });

    feature.epics.forEach((epic: Epic) => {
      epicSet.add(epic);
    });
  });

  const epicMiddleWare = createEpicMiddleware({ dependencies });

  const store = configureStore({
    middleware: [epicMiddleWare, ...middlewares],
    reducer: reducerMap,
    preloadedState,
  });

  epicMiddleWare.run(combineEpics(...Array.from(epicSet)));

  return store;
}
